import { ReactNode, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { AppPathsConsumer } from '../../../AppPathsContext';
import {
  InlineText,
  StyledAnchor,
  StyledNavLink,
  TooltipChild,
  type TooltipPosition,
} from './SmartLink.styles';
import type { SideBarHoverText } from './types';

const TOOLTIP_GAP = 4;

interface SmartLinkProps {
  to: string;
  children: ReactNode;
  toolTip?: SideBarHoverText;
  name?: string;
  className?: string;
}

/**
 * SmartLink renders a sidebar link either as a client-side react-router NavLink
 * (when the target is one of the current app's top-level paths) or as a plain
 * anchor (for links into other apps' bundles, which require a full navigation).
 * As more admin pages are consolidated into a single app, more sidebar links
 * resolve as internal and navigate without a full page reload.
 */
const SmartLink = ({
  to,
  children,
  toolTip,
  name,
  ...rest
}: SmartLinkProps) => {
  const triggerRef = useRef<HTMLAnchorElement>(null);
  // Portaled to document.body with fixed coords so it isn't clipped by the
  // sidebar's scroll container.
  const [tooltipPos, setTooltipPos] = useState<TooltipPosition | null>(null);

  const showTooltip = () => {
    if (!toolTip || !triggerRef.current) return;
    const rect = triggerRef.current.getBoundingClientRect();
    setTooltipPos({
      $top: rect.top + rect.height / 2,
      $left: rect.right + TOOLTIP_GAP,
    });
  };
  const hideTooltip = () => setTooltipPos(null);

  const hoverProps = toolTip
    ? { onMouseEnter: showTooltip, onMouseLeave: hideTooltip }
    : {};

  const inner = (
    <>
      {children}
      {toolTip && tooltipPos
        ? createPortal(
            <TooltipChild {...tooltipPos}>
              <InlineText size="small" fontWeight="bold">
                {toolTip.primary}
              </InlineText>
              <InlineText size="small">&nbsp;{toolTip.secondary}</InlineText>
            </TooltipChild>,
            document.body,
          )
        : null}
    </>
  );

  return (
    <AppPathsConsumer>
      {(appPaths: string[]) => {
        // Internal when the target is one of the current app's owned paths or a
        // sub-route of one (sidebar links are often sub-paths, e.g.
        // `/admin/data-source/roles` under the owned `/admin/data-source`).
        const isInternal =
          Array.isArray(appPaths) &&
          appPaths.some(path => to === path || to.startsWith(`${path}/`));
        return isInternal ? (
          <StyledNavLink
            to={to}
            data-name={name}
            ref={triggerRef}
            {...hoverProps}
            {...rest}
          >
            {inner}
          </StyledNavLink>
        ) : (
          <StyledAnchor
            href={to}
            data-name={name}
            ref={triggerRef}
            {...hoverProps}
            {...rest}
          >
            {inner}
          </StyledAnchor>
        );
      }}
    </AppPathsConsumer>
  );
};

export default SmartLink;
